import { MaxPriorityQueue } from '@datastructures-js/priority-queue';

type Task = (id: string) => Promise<unknown>;

type PreloaderItem = {
  id: string;
  task: Task;
  priority: PreloaderPriority;
};

type PreloaderDelayedItem = PreloaderItem & {
  delay: number;
};

export type PreloaderItemInit = Omit<PreloaderItem, 'id'>;
export enum PreloaderPriority {
  LOW = 1,
  HIGH = 2,
}

export class PreloaderManager {
  public constructor(
    public enabled = true,
    private queue = new MaxPriorityQueue<PreloaderItem>((v) => v.priority),
    private preloading = new Set<string>(),
    private preloaded = new Set<string>(),
    private active = false,
    private timers: ReturnType<typeof setTimeout>[] = []
  ) {}

  scheduleOne = (id: string, init: PreloaderItemInit) => {
    if (!this.enabled) {
      return;
    }

    try {
      this.schedule({
        id,
        task: init.task,
        priority: init.priority,
      });

      this.trigger();
    } catch (err) {
      console.error(err);
    }
  };

  scheduleAll = (entries: Record<string, PreloaderItemInit>) => {
    if (!this.enabled) {
      return;
    }

    try {
      Object.entries(entries).forEach(([id, value]) =>
        this.schedule({
          id,
          task: value.task,
          priority: value.priority,
        })
      );

      this.trigger();
    } catch (err) {
      console.error(err);
    }
  };

  scheduleAllPhased = (entries: Record<string, PreloaderItemInit>, delay = 500) => {
    if (!this.enabled) {
      return;
    }

    try {
      Object.entries(entries).forEach(([id, value], idx) =>
        this.scheduleLater({
          id,
          task: value.task,
          priority: value.priority,
          delay: delay * (idx + 1),
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  private preload = (id: string, task: Task) => {
    return task(id).catch(() => {});
  };

  private trigger = () => {
    if (this.queue.size() === 0 || this.active) {
      return;
    }

    this.tick(this.onTick);
    this.active = true;
  };

  private schedule = (item: PreloaderItem) => {
    if (this.has(item.id)) {
      return;
    }

    this.queue.enqueue(item);
  };

  private scheduleLater = (item: PreloaderDelayedItem) => {
    if (this.has(item.id)) {
      return;
    }

    this.timers.push(
      setTimeout(() => {
        // Check before proceeding once more (async scope)
        if (this.has(item.id)) {
          return;
        }

        this.queue.enqueue(item);
        this.trigger();
      }, item.delay)
    );
  };

  private has = (id: string) => {
    return (
      this.preloading.has(id) ||
      this.preloaded.has(id) ||
      Boolean(this.queue.toArray().find((i) => i.id === id))
    );
  };

  private tick = (cb: VoidFunction) => {
    if (globalThis.requestIdleCallback) {
      globalThis.requestIdleCallback(cb);
    } else {
      this.timers.push(setTimeout(cb, 200));
    }
  };

  private onTick = () => {
    const job = this.queue.dequeue();
    if (job) {
      this.preloading.add(job.id);

      this.preload(job.id, job.task).then(() => {
        this.preloaded.add(job.id);
        this.preloading.delete(job.id);

        if (this.queue.size() !== 0) {
          this.tick(this.onTick);
        } else {
          this.active = false;
        }
      });
    }
  };

  public clear() {
    this.queue.clear();
    this.preloaded.clear();
    this.preloading.clear();

    this.timers.forEach((timer) => {
      clearTimeout(timer);
    });
  }
}
